import { holidayServiceApi } from "../../app/holidayServiceApi";
import { API_URL } from "../../constants/apiUrl";
import {
  GetHolidayByDateParams,
  GetHolidayDetailsResponseType,
  GetHolidayParams,
  GetHolidayResponseType,
  GetQueryArgsType,
  PatchQueryArgsType,
} from "../../types";

export const holidayManagementApi = holidayServiceApi.injectEndpoints({
  endpoints: (builder) => ({
    getHolidays: builder.query<
      GetHolidayResponseType,
      GetQueryArgsType<GetHolidayParams>
    >({
      query: (queryArgs) => ({
        url: API_URL.getHolidays,
        ...queryArgs,
      }),
      providesTags: ["GetHolidays"],
    }),
    getGlobalHolidays: builder.query<
      GetHolidayResponseType,
      GetQueryArgsType<GetHolidayParams>
    >({
      query: (queryArgs) => ({
        url: API_URL.getGlobalHolidays,
        ...queryArgs,
      }),
      providesTags: ["GetHolidays"],
    }),
    getHolidaysByDate: builder.query<
      GetHolidayResponseType,
      GetQueryArgsType<GetHolidayByDateParams>
    >({
      query: (queryArgs) => ({
        url: API_URL.getHolidaysByDate,
        ...queryArgs,
      }),
      providesTags: ["GetHolidaysByDate"],
    }),
    getHolidayDetails: builder.query<GetHolidayDetailsResponseType, string>({
      query: (id) => ({
        url: `${API_URL.getHolidayDetails}/${id}`,
      }),
      providesTags: (_, __, id) => [{ type: "GetHolidayDetails", id }],
    }),
    addHoliday: builder.mutation({
      query: (data) => ({
        url: API_URL.addHoliday,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetHolidays", "GetHolidaysByDate"];
        return [];
      },
    }),
    updateHoliday: builder.mutation<void, PatchQueryArgsType>({
      query: ({ body, id }) => ({
        url: `${API_URL.updateHoliday}/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result, _, { id }) => {
        if (result)
          return [
            "GetHolidays",
            "GetHolidaysByDate",
            {
              type: "GetHolidayDetails",
              id,
            },
          ];

        return [];
      },
    }),
    importGlobalHoliday: builder.mutation({
      query: ({ file, year }: { file: File; year: number }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("year", String(year));

        return {
          url: API_URL.importGlobalHoliday,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (result) => {
        if (result) return ["GetHolidays", "GetHolidaysByDate"];
        return [];
      },
    }),
    deleteGlobalHoliday: builder.mutation({
      query: ({ year }: { year?: number } = {}) => ({
        url: API_URL.importGlobalHoliday,
        method: "DELETE",
        params: year ? { year } : undefined,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetHolidays", "GetHolidaysByDate"];
        return [];
      },
    }),
    deleteSingleGlobalHoliday: builder.mutation({
      query: (payload: {
        title: string;
        start_date: string;
        end_date: string;
      }) => ({
        url: `${API_URL.importGlobalHoliday}/single`,
        method: "DELETE",
        body: payload,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetHolidays", "GetHolidaysByDate"];
        return [];
      },
    }),
  }),
});

export const {
  useGetHolidaysQuery,
  useGetGlobalHolidaysQuery,
  useGetHolidaysByDateQuery,
  useGetHolidayDetailsQuery,
  useAddHolidayMutation,
  useUpdateHolidayMutation,
  useImportGlobalHolidayMutation,
  useDeleteGlobalHolidayMutation,
  useDeleteSingleGlobalHolidayMutation,
} = holidayManagementApi;
